import { useState } from "react";
import { Quote } from "lucide-react";
import { STORIES } from "@/lib/stories";

/**
 * Anonymized employer testimonials as story cards.
 * Props:
 *   - limit: initial visible count (default 6)
 *   - stories: optional custom subset (defaults to full STORIES list)
 *   - testIdPrefix: for data-testid namespacing
 *   - showToggle: whether to render the "Show more / less" toggle
 */
export default function StoryCards({
    limit = 6,
    stories = STORIES,
    testIdPrefix = "story",
    showToggle = true,
}) {
    const [expanded, setExpanded] = useState(false);
    const canToggle = showToggle && stories.length > limit;
    const visible = expanded || !canToggle ? stories : stories.slice(0, limit);

    return (
        <div data-testid={`${testIdPrefix}-cards`}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visible.map((s) => (
                    <figure
                        key={s.id}
                        data-testid={`${testIdPrefix}-card-${s.id}`}
                        className="group flex flex-col justify-between h-full border border-border bg-background p-7 hover:border-brand/60 hover:shadow-sm transition-all"
                    >
                        <div>
                            <Quote
                                className="h-5 w-5 text-brand mb-4"
                                strokeWidth={1.75}
                            />
                            <blockquote className="body text-[0.98rem] leading-relaxed text-foreground">
                                "{s.quote}"
                            </blockquote>
                        </div>
                        <figcaption className="mt-6 pt-5 border-t border-border">
                            <p className="font-heading text-base text-foreground">
                                {s.role}
                            </p>
                            <p className="text-sm text-ink-soft">{s.org}</p>
                            <p className="mt-3 text-xs uppercase tracking-[0.15em] text-navy">
                                {s.tag}
                            </p>
                        </figcaption>
                    </figure>
                ))}
            </div>

            {canToggle && (
                <div className="mt-8 flex justify-center">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`${testIdPrefix}-toggle`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-brand border-b border-foreground hover:border-brand pb-1 transition-colors"
                    >
                        {expanded
                            ? "Show fewer stories"
                            : `Show all ${stories.length} stories`}
                    </button>
                </div>
            )}

            <p className="mt-8 text-xs text-ink-soft text-center max-w-2xl mx-auto">
                Testimonials are from Ignite Health employer partners and have
                been anonymized to protect client privacy.
            </p>
        </div>
    );
}
